import { useCallback, useEffect, useRef, useState } from 'react';

export interface LiveAircraft {
  icao24: string;
  callsign: string | null;
  lat: number;
  lng: number;
  altitude: number | null; // barometric, meters
  velocity: number | null; // m/s
  heading: number | null; // degrees
  onGround: boolean;
  lastSeen: number; // epoch seconds
  originCountry: string;
}

/** Fokusraum: Ostsee / Nordeuropa */
const BBOX = { lamin: 50, lomin: 4, lamax: 63, lomax: 32 };
/** OpenSky free tier: ~400 req/day/IP -> poll every 4 min + manual refresh */
const POLL_MS = 240_000;
const STALE_MS = 3 * 60 * 1000;

/** CORS-Proxy: eigener Worker (env). Ohne Proxy kein Browser-Zugriff — OpenSky erlaubt CORS nur für die eigene Domain. */
const proxy = import.meta.env.VITE_OPENSKY_PROXY as string | undefined;
const proxied = (target: string) =>
  proxy
    ? `${proxy.replace(/\/$/, '')}/states/all?${target.split('?')[1] ?? ''}`
    : target;

export type LiveStatus = 'idle' | 'loading' | 'live' | 'error';

const state = (s: unknown[]): Partial<LiveAircraft> & { lat?: number; lng?: number } => ({
  icao24: String(s[0] ?? ''),
  callsign: typeof s[1] === 'string' && s[1].trim() ? s[1].trim() : null,
  originCountry: String(s[2] ?? ''),
  lastSeen: Number(s[4] ?? 0),
  lng: typeof s[5] === 'number' ? s[5] : undefined,
  lat: typeof s[6] === 'number' ? s[6] : undefined,
  onGround: Boolean(s[8]),
  velocity: typeof s[9] === 'number' ? s[9] : null,
  heading: typeof s[10] === 'number' ? s[10] : null,
  altitude: typeof s[13] === 'number' ? s[13] : null,
});

export function useLiveAircraft(pollMs: number = POLL_MS) {
  const [aircraft, setAircraft] = useState<LiveAircraft[]>([]);
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [error, setError] = useState('');
  const timer = useRef<number | null>(null);

  const fetchOnce = useCallback(async () => {
    try {
      setStatus((prev) => (prev === 'live' ? prev : 'loading'));
      const url =
        `https://opensky-network.org/api/states/all` +
        `?lamin=${BBOX.lamin}&lomin=${BBOX.lomin}&lamax=${BBOX.lamax}&lomax=${BBOX.lomax}`;
      const res = await fetch(proxied(url));
      if (res.status === 403 && !proxy) {
        throw new Error('CORS: OpenSky requires a proxy (VITE_OPENSKY_PROXY).');
      }
      if (!res.ok) throw new Error(`OpenSky HTTP ${res.status}`);
      const data = (await res.json()) as { states?: unknown[][] };
      const now = Date.now() / 1000;
      const list: LiveAircraft[] = (data.states ?? [])
        .map(state)
        .filter((a): a is LiveAircraft => typeof a.lat === 'number' && typeof a.lng === 'number')
        .filter((a) => !a.onGround && now - a.lastSeen < STALE_MS)
        .map(({ icao24, callsign, lat, lng, altitude, velocity, heading, onGround, lastSeen, originCountry }) => ({
          icao24,
          callsign,
          lat: lat as number,
          lng: lng as number,
          altitude,
          velocity,
          heading,
          onGround,
          lastSeen,
          originCountry,
        }));
      setAircraft(list);
      setStatus('live');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, []);

  useEffect(() => {
    void fetchOnce();
    timer.current = window.setInterval(() => void fetchOnce(), pollMs);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [fetchOnce, pollMs]);

  return { aircraft, status, error, refresh: fetchOnce };
}
